import React, {memo} from 'react';

import {BaseUserAvatar} from './BaseUserAvatar';
import {AvatarTooltipsProps} from './tooltips';
import {BasicMaybeTaskUserSerializedType} from './user-type';
import {UserInfoCard} from './UserInfoCard';

interface Props<U extends BasicMaybeTaskUserSerializedType>
  extends AvatarTooltipsProps {
  user: U | null;
  userAvatar?: string;
  className?: string;
  style?: any;
  small?: boolean;
  isGuest?: boolean;
  showCard?: boolean;
}

export const UserAvatar = memo<Props<BasicMaybeTaskUserSerializedType>>(
  function UserAvatar<U extends BasicMaybeTaskUserSerializedType>({
    user,
    className = '',
    small,
    tooltipPlacement,
    tooltipTitle,
    style,
    isGuest,
    showCard = false,
  }: Props<U>) {
    const [showingCard, setShowCard] = React.useState(false);
    const avatarRef = React.useRef<HTMLDivElement>(null);

    const toggleCard = () => {
      setShowCard(!showingCard);
      cardMounted.current = true;
    };

    // we use this flag to postpone card mounting until required
    const cardMounted = React.useRef(false);

    let base = (
      <BaseUserAvatar
        user={user}
        className={className}
        style={style}
        tooltipPlacement={tooltipPlacement}
        tooltipTitle={showingCard ? null : tooltipTitle}
        small={small}
        isGuest={isGuest}
        onClick={showCard ? toggleCard : undefined}
      />
    );

    return (
      <>
        {showCard ? (
          <div ref={avatarRef} style={{cursor: 'pointer'}}>
            {base}
          </div>
        ) : (
          base
        )}
        {showCard &&
        cardMounted.current &&
        avatarRef.current != null &&
        user != null ? (
          <UserInfoCard
            avatarRef={avatarRef.current}
            user={user}
            avatarCircle={base}
            show={showingCard}
            toggle={toggleCard}
          />
        ) : null}
      </>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.user == null || nextProps.user == null) {
      return false;
    }

    return (
      prevProps.user.id === nextProps.user.id &&
      prevProps.user.name === nextProps.user.name &&
      prevProps.user.email === nextProps.user.email &&
      prevProps.user.avatar_image === nextProps.user.avatar_image &&
      prevProps.tooltipTitle === nextProps.tooltipTitle
    );
  }
);
